import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Card, CardContent, Stack, Typography } from '@mui/joy';
import { FaPaperPlane } from 'react-icons/fa';
import { DocumentShareLinksList } from '../../modules/docs/components/DocumentShareLinksList';
import {
  listDocumentShareLinks,
  nullifyDocumentShareLink,
  type DocumentShareLinkRecord,
} from '../../modules/docs/services/documentShareLinks.service';
import { buildDocumentTransmissionVersionInfo } from '../../modules/docs/utils/transmissionInfo';
import {
  classifyTransmissionArtefact,
  filterShareLinksForArtefact,
  type TransmissionArtefact,
  type TransmissionArtefactClassifyArgs,
} from '../../modules/docs/utils/transmissionArtefact';
import { visualDesignerSurveysService } from '../../modules/docs/services/visualDesignerSurveys.service';
import { directSupabase } from '../../lib/supabaseClient';
import type { Document } from '../../modules/docs/types/documents.types';
import { WorkspaceCardHeader } from '../../components/WorkspaceCardHeader';
import FreeAgentTransmissionsList from './FreeAgentTransmissionsList';

type VersionSummary = {
  versionId: string;
  versionNumber: number;
  isTransmitted?: boolean;
  overrideUnlocked?: boolean;
};

export type TransmissionScope = 'all' | 'first' | 'final';

async function resolveVersionSurveyKindById(
  links: DocumentShareLinkRecord[],
): Promise<Record<string, string>> {
  const sentIds = Array.from(new Set(
    links.map((link) => String(link.sentVersionId || '').trim()).filter(Boolean),
  ));
  const sourceIds = Array.from(new Set(
    links.map((link) => String(link.sourceVersionId || '').trim()).filter(Boolean),
  ));
  const versionIds = Array.from(new Set([...sentIds, ...sourceIds]));
  if (versionIds.length === 0) return {};

  const { data: versionRows, error: versionErr } = await directSupabase
    .from('document_versions')
    .select('id, visual_survey_id, cloned_from_version_id')
    .in('id', versionIds);
  if (versionErr) throw new Error(versionErr.message);

  // Only fetch parents for transmission snapshots (sent ids). Final working versions are
  // often cloned_from First — inheriting that parent would mis-label Final as First.
  const parentIds = Array.from(new Set(
    (versionRows || [])
      .filter((row: any) => sentIds.includes(String(row.id || '').trim()))
      .map((row: any) => String(row.cloned_from_version_id || '').trim())
      .filter((id) => id && !versionIds.includes(id)),
  ));
  let parentRows: any[] = [];
  if (parentIds.length > 0) {
    const { data, error } = await directSupabase
      .from('document_versions')
      .select('id, visual_survey_id, cloned_from_version_id')
      .in('id', parentIds);
    if (error) throw new Error(error.message);
    parentRows = data || [];
  }

  const allVersions = [...(versionRows || []), ...parentRows];
  const surveyIds = Array.from(new Set(
    allVersions
      .map((row: any) => String(row.visual_survey_id || '').trim())
      .filter(Boolean),
  ));
  const kindBySurveyId = new Map<string, string>();
  if (surveyIds.length > 0) {
    const { data: surveys, error: surveyErr } = await directSupabase
      .from('visual_designer_surveys')
      .select('id, kind')
      .in('id', surveyIds);
    if (surveyErr) throw new Error(surveyErr.message);
    (surveys || []).forEach((row: any) => {
      kindBySurveyId.set(String(row.id), String(row.kind || '').trim().toLowerCase());
    });
  }

  const surveyIdByVersionId = new Map<string, string>();
  allVersions.forEach((row: any) => {
    const id = String(row.id || '').trim();
    const surveyId = String(row.visual_survey_id || '').trim();
    if (id && surveyId) surveyIdByVersionId.set(id, surveyId);
  });
  // Transmission clones clear visual_survey_id — inherit only onto sent snapshots
  // from their immediate parent (the working version that was transmitted).
  const sentIdSet = new Set(sentIds);
  allVersions.forEach((row: any) => {
    const id = String(row.id || '').trim();
    if (!id || !sentIdSet.has(id) || surveyIdByVersionId.has(id)) return;
    const parent = String(row.cloned_from_version_id || '').trim();
    const parentSurvey = parent ? surveyIdByVersionId.get(parent) : '';
    if (parentSurvey) surveyIdByVersionId.set(id, parentSurvey);
  });

  const out: Record<string, string> = {};
  versionIds.forEach((versionId) => {
    const surveyId = surveyIdByVersionId.get(versionId);
    const kind = surveyId ? kindBySurveyId.get(surveyId) : '';
    if (kind) out[versionId] = kind;
  });
  return out;
}

export default function WorkspaceTransmissionsCard({
  documents,
  versionById = {},
  refreshKey,
  scope = 'all',
  firstWorkingVersionId = null,
  finalWorkingVersionId = null,
  finalSurveyId = null,
  finalReapprovalTransmissionVersionId = null,
  showMismatchAlert = false,
  compact = false,
  title,
  description,
}: {
  documents: Document[];
  versionById?: Record<string, VersionSummary | undefined>;
  refreshKey?: string | number;
  scope?: TransmissionScope;
  firstWorkingVersionId?: string | null;
  finalWorkingVersionId?: string | null;
  finalSurveyId?: string | null;
  finalReapprovalTransmissionVersionId?: string | null;
  showMismatchAlert?: boolean;
  compact?: boolean;
  title?: string;
  description?: string;
  onDocumentsChanged?: () => void;
}) {
  const [linksByDoc, setLinksByDoc] = useState<Record<string, DocumentShareLinkRecord[]>>({});
  const [classifyArgsByDoc, setClassifyArgsByDoc] = useState<Record<string, TransmissionArtefactClassifyArgs>>({});
  const [loading, setLoading] = useState(false);
  const [nullifyingId, setNullifyingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const docs = useMemo(
    () => documents.filter((doc) => doc.kind === 'Estimate' || doc.kind === 'Quotation' || doc.kind === 'Invoice'),
    [documents],
  );
  const invoiceDocs = useMemo(() => docs.filter((doc) => doc.kind === 'Invoice'), [docs]);
  const docKey = docs.map((doc) => `${doc.id}:${doc.updatedAt || ''}`).join('|');

  useEffect(() => {
    let cancelled = false;
    if (docs.length === 0) {
      setLinksByDoc({});
      setClassifyArgsByDoc({});
      return;
    }
    setLoading(true);
    setError(null);
    Promise.all(docs.map(async (doc) => {
      try {
        const links = await listDocumentShareLinks(doc.id);
        let pins = { transmissionVersionIds: [] as string[], shareLinkIds: [] as string[] };
        const finalId = String(finalSurveyId || '').trim();
        if (finalId) {
          try {
            pins = await visualDesignerSurveysService.listFinalReapprovalPins(finalId);
          } catch (err) {
            console.warn('Failed to load Final reapproval pins', err);
          }
        }
        let versionSurveyKindById: Record<string, string> = {};
        try {
          versionSurveyKindById = await resolveVersionSurveyKindById(links);
        } catch (err) {
          console.warn('Failed to resolve transmission survey kinds', err);
        }
        const classifyArgs: TransmissionArtefactClassifyArgs = {
          firstWorkingVersionId,
          finalWorkingVersionId,
          finalReapprovalTransmissionVersionId,
          finalTransmissionVersionIds: pins.transmissionVersionIds,
          finalShareLinkIds: pins.shareLinkIds,
          versionSurveyKindById,
        };
        return [doc.id, links, classifyArgs] as const;
      } catch (err) {
        console.warn('Failed to load transmissions', err);
        return [doc.id, [] as DocumentShareLinkRecord[], {
          firstWorkingVersionId,
          finalWorkingVersionId,
          finalReapprovalTransmissionVersionId,
        } as TransmissionArtefactClassifyArgs] as const;
      }
    }))
      .then((rows) => {
        if (cancelled) return;
        const linksMap: Record<string, DocumentShareLinkRecord[]> = {};
        const argsMap: Record<string, TransmissionArtefactClassifyArgs> = {};
        rows.forEach(([id, links, args]) => {
          linksMap[id] = links;
          argsMap[id] = args;
        });
        setLinksByDoc(linksMap);
        setClassifyArgsByDoc(argsMap);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    docKey,
    refreshKey,
    firstWorkingVersionId,
    finalWorkingVersionId,
    finalSurveyId,
    finalReapprovalTransmissionVersionId,
  ]);

  if (docs.length === 0) return null;

  const nullify = async (link: DocumentShareLinkRecord) => {
    const confirmed = window.confirm('NULLIFY this transmission link? This immediately blocks customer access for this URL only.');
    if (!confirmed) return;
    const reason = window.prompt('Optional nullify reason (internal audit):', '') || '';
    setNullifyingId(link.id);
    setError(null);
    try {
      await nullifyDocumentShareLink(link.id, reason);
      const refreshed = await listDocumentShareLinks(link.documentId);
      setLinksByDoc((prev) => ({ ...prev, [link.documentId]: refreshed }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to nullify link');
    } finally {
      setNullifyingId(null);
    }
  };

  const headerTitle = title
    || (scope === 'first'
      ? 'First Survey transmissions'
      : scope === 'final'
        ? 'Final Survey transmissions'
        : 'Transmissions');
  const headerDescription = description
    || (scope === 'all'
      ? 'Public links sent to the customer.'
      : 'Customer links for this survey.');

  const body = (
    <Stack gap={1.25}>
      {error && <Alert color="danger">{error}</Alert>}
      {docs.map((doc) => {
        const allLinks = linksByDoc[doc.id] || [];
        const classifyArgs = classifyArgsByDoc[doc.id] || {
          firstWorkingVersionId,
          finalWorkingVersionId,
          finalReapprovalTransmissionVersionId,
        };
        const links = filterShareLinksForArtefact(allLinks, scope, classifyArgs);
        const version = versionById[doc.id];
        const scopedWorkingId = scope === 'final'
          ? (finalWorkingVersionId || version?.versionId || null)
          : scope === 'first'
            ? (firstWorkingVersionId || version?.versionId || null)
            : (version?.versionId || null);
        const transmission = doc.kind === 'Invoice' || !showMismatchAlert
          ? null
          : buildDocumentTransmissionVersionInfo({
            activeVersionId: scopedWorkingId,
            activeVersionNumber: version?.versionNumber ?? null,
            activeIsTransmitted: version?.isTransmitted,
            activeOverrideUnlocked: version?.overrideUnlocked,
            shareLinks: links,
          });
        const artefactByLinkId: Record<string, TransmissionArtefact> = Object.fromEntries(
          links.map((row) => [
            row.id,
            classifyTransmissionArtefact(row, classifyArgs),
          ]),
        );

        return (
          <Stack key={doc.id} gap={1}>
            {docs.length > 1 && scope === 'all' && (
              <Typography level="title-sm">
                {doc.kind === 'Invoice' && doc.invoiceRef ? doc.invoiceRef : doc.lifecycleRef}
                {' '}
                <Typography component="span" level="body-xs" sx={{ color: 'neutral.500' }}>
                  {doc.kind}
                </Typography>
              </Typography>
            )}
            {transmission?.hasMismatch && (
              <Alert color="warning" variant="soft">
                This survey no longer matches the latest customer transmission
                {transmission.transmittedAt
                  ? ` (last sent ${new Date(transmission.transmittedAt).toLocaleString('en-GB')})`
                  : ''}.
              </Alert>
            )}
            <DocumentShareLinksList
              links={links}
              loading={loading}
              showVersionLabels={false}
              showArtefactLabels={scope === 'all'}
              artefactByLinkId={artefactByLinkId}
              emptyLabel={
                scope === 'final'
                  ? 'No Final Survey transmissions yet.'
                  : scope === 'first'
                    ? 'No First Survey transmissions yet.'
                    : 'No public transmissions yet.'
              }
              openLinkLabel={
                scope === 'final'
                  ? 'Open Final transmission'
                  : scope === 'first'
                    ? 'Open First transmission'
                    : 'Open transmission'
              }
              nullifyingId={nullifyingId}
              onNullify={(row) => { void nullify(row); }}
            />
          </Stack>
        );
      })}
      {invoiceDocs.length > 0 && scope === 'all' && (
        <FreeAgentTransmissionsList invoices={invoiceDocs} refreshKey={refreshKey} />
      )}
    </Stack>
  );

  if (compact) {
    return (
      <Stack gap={1}>
        <Typography level="title-sm">{headerTitle}</Typography>
        <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
          {headerDescription}
        </Typography>
        {body}
      </Stack>
    );
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <WorkspaceCardHeader
          icon={FaPaperPlane}
          title={headerTitle}
          description={headerDescription}
        />
        {body}
      </CardContent>
    </Card>
  );
}
